import { IAuthRepository } from '../../domain/repositories/IAuthRepository';
import { User } from '../../domain/entities/User';
import { Email } from '../../domain/valueObjects/Email';
import { Password } from '../../domain/valueObjects/Password';
import { ConfigManager } from '../../application/config/Config';
import { LoggerFactory } from '../../application/logging/Logger';
import { AuthenticationError } from '../../application/errors/AppError';
import { SharetribeRepository } from '../../infrastructure/repositories/SharetribeRepository';
import { apiService } from '@/services';
import {  CreateSharetribeUserRequest } from '../../types';
import { secureStorage } from "@/utils/secureStorage";


/**
 * API-based Authentication Repository Implementation
 * Follows Repository Pattern and Dependency Inversion Principle
 */
export class AuthRepository implements IAuthRepository {
  private readonly config = ConfigManager.getInstance().getApiConfig();
  private readonly logger = LoggerFactory.getLogger();
  
  private readonly sharetribeRepo = new SharetribeRepository(apiService); 

    async login(email: Email, password: Password): Promise<{ user: User; token: string }> {
      try {
        // 1) call primary API (same as you already had)
        const response = await fetch(`${this.config.baseUrl}/login/check`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-API-Key': this.config.apiKey
          },
          body: new URLSearchParams({
            email: email.value,
            password: password.value,
            deviceToken: 'web-app'
          })
        });

        if (!response.ok) {
          throw new AuthenticationError('Login failed');
        }

        const data = await response.json();

        if (data.status === 0 || data.status === false) {
          this.logger.error('API returned failure status', new Error('API failure'), { data });
          throw new AuthenticationError(data.message || 'Login failed');
        }

        // Extract token & user data from swimnerd API
        const token = data.access_token || data.token || data.data?.access_token || data.data?.token;
        if (!token) {
          this.logger.error('No token found in response', new Error('No token'), { data });
          throw new AuthenticationError('No access token received');
        }

        const userData = data.data || data;
        const detectedType: 'swimmer' | 'coach' = userData.coach_id ? 'coach' : 'swimmer';
        const user = User.fromData({
          id: userData.id,
          email: userData.email,
          fname: userData.fname,
          lname: userData.lname,
          image: userData.image,
          type: detectedType
        });

        // persist basic values from swimnerd API
        if (userData.coach_id) localStorage.setItem('coach_id', String(userData.coach_id));
        if (userData.id) localStorage.setItem('current_user_swimmer_id', String(userData.id));
        if (userData.email) secureStorage.set('current_user_email', userData.email);
        if (userData.fname || userData.lname) secureStorage.set('current_user_name', `${userData.fname} ${userData.lname}`);
        if (userData.image) secureStorage.set('current_user_pic', userData.image);
        secureStorage.set('has_blog_access', Boolean(userData.has_blog_access));
        secureStorage.set('has_pool_access', Boolean(userData.has_pool_access));
        // ---------- Begin Sharetribe flow  ----------
        //  login to Sharetribe

        const numericId = parseInt(String(userData.id).replace(/\D/g, '').slice(0, 8)) || 0;
        const paddedIdPassword = this.FormatToEightDigits(numericId);

        // Try login with padded-id password first
        let sharetribeSession: any | null = null;
        try {
          sharetribeSession = await this.sharetribeRepo.login(email.value, paddedIdPassword);
          this.logger.info('Sharetribe login attempt with padded id password', { email: email.value, success: !!sharetribeSession });
        } catch (err) {
          this.logger.warn('Sharetribe login (padded-id) failed');
          sharetribeSession = null;
        }

        // If padded-id login failed, try login with the user's actual password
        if (!sharetribeSession) {
          try {
            sharetribeSession = await this.sharetribeRepo.login(email.value, password.value);
            this.logger.info('Sharetribe login attempt with actual password', { email: email.value, success: !!sharetribeSession });
          } catch (err) {
            this.logger.warn('Sharetribe login (actual password) failed');
            sharetribeSession = null;
          }

          // If actual-password login succeeded, update Sharetribe password to paddedIdPassword
          if (sharetribeSession) {
            try {
              const changeResult = await this.sharetribeRepo.changePassword(password.value, paddedIdPassword);
              // changeResult contract may vary; assume null/undefined means error, else success
              if (changeResult && changeResult.code) {
                // If Sharetribe returns a non-empty error code -> treat as failure
                this.logger.warn('Sharetribe changePassword returned error', { code: changeResult.code });
                // keep going — you can fail early if you want
              } else {
                this.logger.info('Sharetribe password updated to padded id password');
              }
            } catch (err) {
              this.logger.warn('Sharetribe changePassword failed');
            }
          }
        }

        // If both logins failed, create the Sharetribe user (paddedIdPassword)
        if (!sharetribeSession) {
          try {
            const createPayload: CreateSharetribeUserRequest = {
              email: user.email,
              firstName: user.firstName,
              lastName: user.lastName,
              role: detectedType,
              password: paddedIdPassword,
              swimnerdUserId: String(user.id)
            };

            const createResult = await this.sharetribeRepo.createUser(createPayload);
            // handle email-taken specifically
            if (createResult && (createResult as any).code === 'email-taken') {
              // If Sharetribe says email taken, try to login using padded password (maybe the account exists but different password)
              this.logger.warn('Sharetribe createUser: email-taken. Attempting login with padded id password.');
              try {
                sharetribeSession = await this.sharetribeRepo.login(email.value, paddedIdPassword);
                this.logger.info('Sharetribe login after email-taken create attempt', { success: !!sharetribeSession });
              } catch (err) {
                this.logger.error('Sharetribe login after email-taken failed', err as Error);
              }
            } else {
              // If createResult was successful or had no error code, attempt login
              try {
                sharetribeSession = await this.sharetribeRepo.login(email.value, paddedIdPassword);
                this.logger.info('Sharetribe login after createUser', { success: !!sharetribeSession });
              } catch (err) {
                this.logger.error('Sharetribe login after createUser failed', err as Error);
              }
            }
          } catch (err) {
            this.logger.error('Sharetribe createUser failed', err as Error);
          }
        }

        // At this point we either have a sharetribeSession or not. Continue to fetch current user if possible.
        let sharetribeCurrentUser: any | null = null;
        try {
          sharetribeCurrentUser = await this.sharetribeRepo.currentUserShow(true); // pass `true` for expanded includes if your repo supports that param
          this.logger.info('Sharetribe CurrentUser fetched', { id: sharetribeCurrentUser?.id });
        } catch (err) {
          this.logger.warn('Sharetribe getCurrentUser failed');
          sharetribeCurrentUser = null;
        }

        // If we were able to fetch Sharetribe user, validate emailVerified and publicData
        if (sharetribeCurrentUser) {
          const emailVerified = !!(sharetribeCurrentUser?.attributes?.emailVerified);
          if (!emailVerified) {
            // If not verified, clear stored swimnerd token (mimic C# Preferences.Set(..., string.Empty))
            localStorage.removeItem('auth_token');
            // throw or return a specific error so caller can redirect to verification flow
            throw new AuthenticationError('Email not verified in Sharetribe');
          }

          const publicData = sharetribeCurrentUser?.attributes?.profile?.publicData || {};
          const expectedType = detectedType;


          if (String(publicData.type) !== expectedType) {
            // type mismatch 
            this.logger.error('Sharetribe publicData.type mismatch');
            throw new AuthenticationError('Failed to login (type mismatch)');
          }

          // If publicData.user_id is not our swimnerd user id, update sharetribe profile
          if (String(publicData.user_id) !== String(user.id)) {
            const dataPublicUpdate: Record<string, string> = {
              type: expectedType,
              user_id: String(user.id)
            };


            try {
              await this.sharetribeRepo.updateProfile(null, null, null, null, dataPublicUpdate, null, null, null);
              this.logger.info('Sharetribe profile publicData updated', { userId: user.id });
            } catch (err) {
              this.logger.warn('Sharetribe updateProfile failed');
            }
          }

          // cache the flex user id (Sharetribe id)
          if (sharetribeCurrentUser?.id) {
            localStorage.setItem('flex_user_id', sharetribeCurrentUser.id);
          }
        } else {
          // If we couldn't fetch the Sharetribe current user, continue — optionally warn
          this.logger.warn('No Sharetribe current user fetched; continuing without Sharetribe profile validation.');
        }

        // ---------- End Sharetribe flow ----------

        // Finally return user + token (token from swimnerd)
        this.logger.info('User login successful', {
          userId: user.id,
          email: user.email,
          type: detectedType
        });

        secureStorage.set('user_type' , detectedType);

        // Save swimnerd token to local storage if you want (mimic Preferences.Set)
        localStorage.setItem('auth_token', token);
        if (sharetribeSession) {
           sharetribeCurrentUser = await this.sharetribeRepo.sharedTribecurrentUserShow(true);
           this.logger.info('sharetribe current user' , sharetribeCurrentUser);
          localStorage.setItem("flex_token", sharetribeSession.access_token || sharetribeSession);
          if (sharetribeSession.refresh_token) {
            localStorage.setItem("flex_refresh_token", sharetribeSession.refresh_token);
          }
        }
        return { user, token };
      } catch (error) {
        this.logger.error('Login failed', error as Error, { email: email.value });
        throw error;
      }
    }

  async register(userData: {
      email: Email;
      password: Password;
      firstName: string;
      lastName: string;
      type: 'swimmer' | 'coach';
      flexId?: string;
    }): Promise<{ user: User; token: string | null }> {
      try {
        const response = await fetch(`${this.config.baseUrl}/login/signup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-API-Key': this.config.apiKey
          },
          body: new URLSearchParams({
              email: userData.email.value,
              pwd: userData.password.value,
              fname: userData.firstName.trim(),
              lname: userData.lastName.trim(),
              type: userData.type,
              flex_id: userData.flexId || ''
            })

        });

        if (!response.ok) {
          throw new AuthenticationError('Registration failed');
        }

        const data = await response.json();

        if (data.status === 0 || data.status === false) {
        throw new AuthenticationError(data.msg || data.message || 'Registration failed');
      }

        const userDataResponse = data.data || data;

        const user = User.fromData({
          id: userDataResponse.id,
          email: userDataResponse.email,
          fname: userDataResponse.fname,
          lname: userDataResponse.lname,
          type: userDataResponse.type || userData.type
        });

      const numericId = parseInt(userDataResponse.id.replace(/\D/g, '').slice(0, 8)) || 0;
      const password = this.FormatToEightDigits(numericId);

        const sharetribePayload: CreateSharetribeUserRequest = {
          email: user.email,
          firstName: userData.firstName,  
          lastName: userData.lastName,
          role: userData.type,
          password: password,
          swimnerdUserId: user.id
        };

        try {
          const sharetribeUser = await this.sharetribeRepo.createUser(sharetribePayload);

          this.logger.info('Sharetribe user created successfully', {
            sharetribe_id: sharetribeUser.id,
            swimnerd_user_id: user.id
          });
        } catch (err) {
          this.logger.error('Sharetribe user creation failed', err as Error);
        }

        localStorage.setItem('current_user_swimmer_id', userDataResponse.id);
        secureStorage.set('current_user_email', userDataResponse.email);
        secureStorage.set('current_user_name', `${userDataResponse.fname} ${userDataResponse.lname}`);
        secureStorage.set('has_blog_access', Boolean(userDataResponse.has_blog_access));
        secureStorage.set('has_pool_access', Boolean(userDataResponse.has_pool_access));

        if (userDataResponse.coach_id) {
          localStorage.setItem('coach_id', userDataResponse.coach_id);
        }

        this.logger.info('User registration successful', {
          userId: user.id,
          email: user.email
        });

        return { user, token: null };
      } catch (error) {
        this.logger.error('Registration failed', error as Error, {
          email: userData.email.value
        });
        throw error;
      }
    }

  FormatToEightDigits(id: number) {
  return id.toString().padStart(8, "0");
}

  async validateToken(token: string): Promise<User | null> {
    try {
      const response = await fetch(`${this.config.baseUrl}/auth/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-API-Key': this.config.apiKey
        }
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      
      const user = User.fromData({
        id: data.id,
        email: data.email,
        fname: data.fname,
        lname: data.lname,
        type: data.type
      });

      this.logger.info('Token validation successful', { userId: user.id });
      return user;
    } catch (error) {
      this.logger.error('Token validation failed', error as Error);
      return null;
    }
  }

  async refreshToken(token: string): Promise<string> {
    try {
      const response = await fetch(`${this.config.baseUrl}/auth/refresh-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-API-Key': this.config.apiKey
        }
      });

      if (!response.ok) {
        throw new AuthenticationError('Token refresh failed');
      }

      const data = await response.json();
      return data.token || data.access_token;
    } catch (error) {
      this.logger.error('Token refresh failed', error as Error);
      throw error;
    }
  }

async forgotPassword(email: string): Promise<{ status: number; msg: string }> {
  const params = new URLSearchParams();
  params.append("email", email);

  const response = await fetch(`${this.config.baseUrl}/login/forgot_password`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: params.toString(),
  });

  if (!response.ok) {
    throw new Error(`Failed to send reset email: ${response.statusText}`);
  }

  const data = await response.json();
  return data;
}

  async resetPassword(email: Email, otp: string, newPassword: Password): Promise<void> {
    try {
      const response = await fetch(`${this.config.baseUrl}/login/reset_password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-API-Key': this.config.apiKey
        },
        body: new URLSearchParams({
          email: email.value,
          otp,
          new_password: newPassword.value
        })
      });

      if (!response.ok) {
        throw new AuthenticationError('Password reset failed');
      }

      this.logger.info('Password reset successful', { email: email.value });
    } catch (error) {
      this.logger.error('Password reset failed', error as Error, { email: email.value });
      throw error;
    }
  }

async changePassword(email: string, currentPassword: Password, newPassword: Password, token?: string): Promise<any> {
  const response = await fetch(`${this.config.baseUrl}/user/change_password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Authorization': `Bearer ${token || localStorage.getItem('auth_token')}`,
      'X-API-Key': this.config.apiKey
    },
    body: new URLSearchParams({
      email,
      old_password: currentPassword.value,
      new_password: newPassword.value
    })
  });

  const data = await response.json();
  return data;
}

  async logout(token: string): Promise<void> {
    try {
      // In a real implementation, you might want to invalidate the token on the server
      this.logger.info('User logout', { token: token.substring(0, 10) + '...' });
    } catch (error) {
      this.logger.error('Logout failed', error as Error);
    }
  }
}
